import { IconButton, Box } from "@mui/material";
import React, { useState, useCallback, useMemo, memo } from "react";
import { Panel, useReactFlow } from "reactflow";
import { StriimInputField } from "@striim/striim-ui-v2";

import WithTooltip from "src/generic/tooltip/tooltip";
import { NodeIcon } from "../../nodes/node.commons";
import { components, OptionProps } from "react-select";
import Binoculars from "../../../../../generic/icon/customIcons/binoculars.svg";
import MultiSelectIcon from "app/images/striimline/multiselect.svg";
import { flowControlStyles, FLOW_CONTROL_COLORS } from "../../components/flow-controls.styles";

type NodeOption = { label: string; value: string; data: any };

const multiSelectButtonStyles = {
    ...flowControlStyles.panel,
    width: "36px",
    height: "36px",
    color: FLOW_CONTROL_COLORS.iconDefault,
    "& svg": { width: "20px", height: "20px" }
};

const multiSelectButtonActiveStyles = {
    ...multiSelectButtonStyles,
    border: "2px solid #089BEE"
};
const iconButtonStyles = {
    color: "#6e7d8c",
    "& svg": {
        fill: "none",
        width: "24px",
        height: "24px"
    },
    ...flowControlStyles.panel
};

// Defined outside component so react-select never re-mounts options on re-render
const CustomOption = (props: OptionProps<NodeOption, false>) => {
    const { label, data } = props.data;
    return (
        <components.Option {...props} innerProps={{ ...props.innerProps, style: { padding: 0 } }}>
            <Box sx={{ display: "flex", alignItems: "center", pl: 1 / 2 }}>
                <NodeIcon data={data} showBorder={false} />
                <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</Box>
            </Box>
        </components.Option>
    );
};

// Stable reference — react-select bails out of re-rendering options only when this object is stable
const SELECT_COMPONENTS = { Option: CustomOption };

type Props = {
    onNodeSelect: (nodeId: string | null) => void;
    isMultiSelectActive: boolean;
    onToggleMultiSelect: () => void;
};

const TopRightPanel = memo(({ onNodeSelect, isMultiSelectActive, onToggleMultiSelect }: Props) => {
    const { getNodes } = useReactFlow();
    const [selectedNode, setSelectedNode] = useState<string | null>(null);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [selectOptions, setSelectOptions] = useState<Array<{ label: string; options: NodeOption[] }>>([]);

    const handleNodeChange = useCallback(
        (value: { label: string; value: string | number } | null) => {
            const nodeId = value?.value ? String(value.value) : null;
            setSelectedNode(nodeId);
            onNodeSelect(nodeId);
        },
        [onNodeSelect]
    );

    const handleSearchClick = useCallback(() => {
        // getNodes() reads the current snapshot without subscribing — no re-renders on node changes
        const grouped = getNodes().reduce((acc, node) => {
            const nodeType = node.type || "OTHER";
            if (!acc[nodeType]) acc[nodeType] = [];
            acc[nodeType].push({
                label: node.data?.label || node.id,
                value: node.id,
                data: node.data
            });
            return acc;
        }, {} as Record<string, NodeOption[]>);

        setSelectOptions(Object.entries(grouped).map(([type, options]) => ({ label: type, options })));
        setIsSearchOpen(true);
    }, [getNodes]);

    const handleSearchBlur = useCallback(() => {
        if (!selectedNode) setIsSearchOpen(false);
    }, [selectedNode]);

    const selectedValue = useMemo(() => {
        if (!selectedNode) return null;
        const node = getNodes().find(n => n.id === selectedNode);
        if (!node) return null;
        return { label: node.data?.label || node.id, value: node.id, data: node.data };
    }, [selectedNode, getNodes]);

    return (
        <Panel position="top-right">
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {!isSearchOpen ? (
                    WithTooltip(
                        <IconButton
                            onClick={handleSearchClick}
                            size="small"
                            sx={iconButtonStyles}
                            data-test-id="search-nodes-button"
                        >
                            <Binoculars />
                        </IconButton>,
                        "Find component"
                    )
                ) : (
                    <Box sx={{ minWidth: 300 }} data-test-id="search-nodes-select">
                        <StriimInputField
                            SelectProps={{
                                options: selectOptions,
                                components: SELECT_COMPONENTS
                            }}
                            select
                            value={selectedValue}
                            onChange={handleNodeChange}
                            onBlur={handleSearchBlur}
                            placeholder="Find component..."
                            autoFocus
                        />
                    </Box>
                )}
                {WithTooltip(
                    <IconButton
                        onClick={onToggleMultiSelect}
                        size="small"
                        sx={isMultiSelectActive ? multiSelectButtonActiveStyles : multiSelectButtonStyles}
                        data-test-id="multi-select-button"
                        aria-pressed={isMultiSelectActive}
                    >
                        <MultiSelectIcon />
                    </IconButton>,
                    isMultiSelectActive ? "Disable multi-selection" : "Enable multi-selection"
                )}
            </Box>
        </Panel>
    );
});

export default TopRightPanel;
